/**
 * Vincent (2026-07-22) — P1-4 Phase 3 refactor : hook encapsulant le
 * chargement des transcripts multi-sources (Whisper + Fireflies + Fusionnée)
 * via GET /api/videos/{id}/transcripts/all.
 *
 * Vincent B685 (Eric 2026-02-XX) — Migration TanStack Query v5 :
 *   - Cache automatique via queryKey ['transcripts', videoId] (dédup + share
 *     entre composants qui utilisent le même videoId).
 *   - Pas de spinner intermédiaire sur les refetch en arrière-plan.
 *   - `loadAllTranscripts` = refetch() manuel (avec préservation scrollTop).
 *   - `setAllTranscripts` = mutation du cache via queryClient.setQueryData
 *     (compatibilité API : les composants peuvent toujours faire
 *     `setAllTranscripts((prev) => ...)`).
 *
 * Vincent B956 partie 2 (Eric 2026-02-14 SPEC STRICK PROD) — Pagination
 * progressive des segments Whisper. Sur une vidéo de 90 min avec 5000+
 * segments, le payload initial pesait 1-2 MB → bloquait le rendu 500-800ms
 * sur PROD (Tier 0). Nouveau flux :
 *   1. Fetch initial `whisper_page_size=100` (rendu rapide, ~50-100kB).
 *   2. Un useEffect détecte `whisper.has_more_segments === true` et fetch
 *      les pages suivantes en série (une page = une requête) puis merge
 *      dans le cache via `setAllTranscripts` (concat des segments).
 *   3. Les autres consommateurs (TranscriptPanel, etc.) reçoivent
 *      incrémentalement les segments — le tri par `start` reste valide car
 *      chaque page arrive dans l'ordre chronologique.
 *
 * Contrat inchangé :
 *   const { allTranscripts, setAllTranscripts, transcriptsLoading,
 *           loadAllTranscripts } =
 *     useTranscriptGeneration({ videoId, api, onHydrateRejected });
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Vincent B956 partie 2 — Taille de page fixe (100 segments), simple et
// prédictible pour le backfill progressif. On peut monter à 200 si la
// latence Mongo devient un problème mais 100 est un bon compromis
// payload/latence sur le pod Tier 0.
export const WHISPER_PAGE_SIZE = 100;

const fetchAllTranscripts = async ({ queryKey }) => {
  const [, videoId, api] = queryKey;
  const { data } = await axios.get(
    `${api}/api/videos/${videoId}/transcripts/all`,
    {
      params: { whisper_page: 0, whisper_page_size: WHISPER_PAGE_SIZE },
      withCredentials: true,
      // Vincent B1015 (Eric 2026-02-26) — Polling → 1 retry max.
      _maxRetries: 1,
      silent: true,
    },
  );
  return data;
};

export function useTranscriptGeneration({ videoId, api, onHydrateRejected }) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["transcripts", videoId, api], [videoId, api]);

  const {
    data: allTranscripts = null,
    isFetching: transcriptsLoading,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: fetchAllTranscripts,
    enabled: !!videoId,
    staleTime: 30_000,
  });

  // Vincent B685 (Eric 2026-02-XX) — Hydrate rejected corrections après chaque
  // succès de fetch. useRef pour comparer et n'appeler onHydrateRejected qu'à
  // chaque nouvelle donnée (pas à chaque render du composant parent).
  const lastHydratedRef = useRef(null);
  useEffect(() => {
    if (!allTranscripts) return;
    if (lastHydratedRef.current === allTranscripts) return;
    lastHydratedRef.current = allTranscripts;
    const hydrated = [];
    const mergedSegs = allTranscripts?.merged?.segments || [];
    for (const s of mergedSegs) {
      for (const c of (s.corrections || [])) {
        if (c.rejected) hydrated.push(`${s.id}::${c.original}::${c.correction}`);
      }
    }
    if (typeof onHydrateRejected === "function") onHydrateRejected(hydrated);
  }, [allTranscripts, onHydrateRejected]);

  // API : mise à jour manuelle du cache (compat avec ancien
  // `setAllTranscripts` — accepte soit une valeur directe, soit un updater).
  const setAllTranscripts = useCallback(
    (updater) => {
      queryClient.setQueryData(queryKey, (prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
    },
    [queryClient, queryKey],
  );

  // Vincent B956 partie 2 — Backfill progressif des pages Whisper suivantes.
  // On surveille `whisper.has_more_segments` : à chaque hydratation, si vrai,
  // on fetch la page suivante en arrière-plan et on concatène. `inFlightRef`
  // empêche le double-fetch en cas de re-render, `abortedRef` coupe si le
  // videoId change en cours de backfill.
  const backfillInFlightRef = useRef(false);
  const backfillAbortedRef = useRef(false);
  useEffect(() => {
    // Reset flags à chaque changement de videoId.
    backfillAbortedRef.current = false;
    return () => {
      backfillAbortedRef.current = true;
    };
  }, [videoId]);
  useEffect(() => {
    if (!videoId || !allTranscripts?.whisper) return;
    const w = allTranscripts.whisper;
    if (!w.has_more_segments) return;
    if (backfillInFlightRef.current) return;
    if (backfillAbortedRef.current) return;
    backfillInFlightRef.current = true;
    (async () => {
      try {
        const nextPage = (w.segments_page ?? 0) + 1;
        const { data: page } = await axios.get(
          `${api}/api/videos/${videoId}/transcripts/all`,
          {
            params: {
              whisper_page: nextPage,
              whisper_page_size: WHISPER_PAGE_SIZE,
            },
            withCredentials: true,
            // Vincent B1015 (Eric 2026-02-26) — Backfill paginé sur boucle
            // : réduire les retries pour éviter le retry storm.
            _maxRetries: 2,
          },
        );
        if (backfillAbortedRef.current) return;
        const newSegs = page?.whisper?.segments || [];
        setAllTranscripts((prev) => {
          if (!prev?.whisper) return prev;
          const prevIds = new Set(
            (prev.whisper.segments || []).map((s) => s.id),
          );
          const merged = [
            ...(prev.whisper.segments || []),
            ...newSegs.filter((s) => !prevIds.has(s.id)),
          ];
          return {
            ...prev,
            whisper: {
              ...prev.whisper,
              segments: merged,
              segments_page: page?.whisper?.segments_page ?? nextPage,
              has_more_segments:
                page?.whisper?.has_more_segments ?? false,
              segments_total:
                page?.whisper?.segments_total ??
                prev.whisper.segments_total,
            },
          };
        });
      } catch {
        // silencieux — sur échec, has_more_segments reste true et un
        // prochain refetch tentera à nouveau.
      } finally {
        backfillInFlightRef.current = false;
      }
    })();
  }, [videoId, api, allTranscripts, setAllTranscripts]);

  // API : loadAllTranscripts avec préservation scrollTop (bug #136 Vincent).
  const loadAllTranscripts = useCallback(async (preserveScrollSegmentId = null) => {
    const scrollContainer = preserveScrollSegmentId
      ? document.querySelector('[data-testid="merged-container"]') ||
        document.querySelector('[data-testid="merged-segments-list"]') ||
        document.scrollingElement
      : null;
    const savedScrollTop = scrollContainer?.scrollTop ?? 0;

    // Reset hydration guard : force onHydrateRejected à re-tirer après refetch.
    lastHydratedRef.current = null;
    await refetch();

    if (preserveScrollSegmentId) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const newScroll = document.querySelector('[data-testid="merged-container"]') ||
                            document.querySelector('[data-testid="merged-segments-list"]') ||
                            document.scrollingElement;
          if (newScroll) {
            newScroll.scrollTop = savedScrollTop;
          }
          const el = document.querySelector(`[data-seg-id="${preserveScrollSegmentId}"]`);
          if (el && typeof el.scrollIntoView === "function") {
            const rect = el.getBoundingClientRect();
            const inView = rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
            if (!inView) el.scrollIntoView({ block: "nearest", behavior: "auto" });
          }
        });
      });
    }
  }, [refetch]);

  return { allTranscripts, setAllTranscripts, transcriptsLoading, loadAllTranscripts };
}

export default useTranscriptGeneration;
